import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useAuth } from '../components/auth/AuthProvider';
import { useRedirectIfLoggedIn } from '../hooks/use-auth-hooks';
import { clearAllAuthData } from '../utils/auth-utils';
import { LoginForm } from '@/components/login-form';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CheckCircle } from 'lucide-react';

export function LoginPage() {
  const { login, googleAuth, error } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const resetSuccess = searchParams.get('resetSuccess') === 'true';
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [signupMessage, setSignupMessage] = useState<string | null>(null);
  const [prefillEmail, setPrefillEmail] = useState<string>('');
  
  // Set page title
  useEffect(() => {
    document.title = 'Authentication - AceHorizon';
  }, []);
  
  // Clear any stale auth data if coming from password reset
  useEffect(() => {
    const comingFromReset = resetSuccess || location.state?.fromPasswordReset;
    if (comingFromReset) {
      console.log('Coming from password reset, ensuring clean auth state');
      clearAllAuthData();
    }
  }, [resetSuccess, location.state]);
  
  // Check for signup success message
  useEffect(() => {
    if (location.state?.message) {
      setSignupMessage(location.state.message);
      if (location.state?.email) {
        setPrefillEmail(location.state.email);
      }
      // Clear the state to prevent showing the message on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);
  
  // Redirect if already logged in
  useRedirectIfLoggedIn();

  const { user: authUser } = useAuth(); // Get direct reference to user state
  
  const handleSubmit = async (email: string, password: string) => {
    try {
      setIsSubmitting(true);
      console.log('LoginPage: Starting email login process for', email);
      
      // Perform login
      const result = await login(email, password);
      console.log('LoginPage: Login result:', result);
      
      if (result.error) {
        console.error('LoginPage: Login error from auth provider:', result.error);
        return; // Don't navigate if there's an error
      }
      
      console.log('LoginPage: Email login completed successfully');
      
      // For email login, navigate to dashboard immediately
      // The profile context will handle fetching profile and showing completion dialog
      navigate('/dashboard', { replace: true });
      
    } catch (error) {
      console.error('LoginPage: Unexpected error during email login:', error);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleGoogleLogin = async () => {
    try {
      setIsSubmitting(true);
      console.log('LoginPage: Starting Google login process');
      const result = await googleAuth();
      
      // Check if there was an error
      if (result.error) {
        console.error('LoginPage: Google auth failed:', result.error);
        setIsSubmitting(false);
        return;
      }
      
      // If we reach here and haven't redirected within 3 seconds, something went wrong
      setTimeout(() => {
        console.warn('LoginPage: Expected redirect to Google OAuth but still on this page');
        setIsSubmitting(false);
      }, 3000);
      
      // Note: For Google login, we don't navigate here because it will redirect
      // to the OAuth provider and then back to auth-callback-page
    } catch (error) {
      console.error('LoginPage: Google login failed:', error);
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      
      {signupMessage && (
        <Alert className="mb-4 bg-green-50 text-green-800 border-green-200">
          <CheckCircle className="h-4 w-4" />
          <AlertDescription>{signupMessage}</AlertDescription>
        </Alert>
      )}
      
      {resetSuccess && (
        <Alert className="mb-4 bg-green-50 text-green-800 border-green-100">
          <AlertDescription>Your password has been reset successfully. You can now log in with your new password.</AlertDescription>
        </Alert>
      )}
      
      <LoginForm 
        className="w-full" 
        onSubmit={handleSubmit}
        onGoogleLogin={handleGoogleLogin}
        isSubmitting={isSubmitting}
        defaultEmail={prefillEmail}
      />
    </div>
  );
}
